import { useEffect, useState } from 'react';
import { getShareLinks, createShareLink, revokeShareLink, type ShareLink } from '../account';

/**
 * Read-only links into this account, for agencies.
 *
 * You name a link after the person it is for, send them the URL, and they see
 * everything and can export it. They cannot scan, spend or change anything; the
 * server refuses that, not this page.
 *
 * The URL is shown once. A secret that can be re-read is a secret sitting in a
 * page somebody will screenshot, which is the same rule the plugin download link
 * already follows.
 */
export default function ShareLinks({ domains }: { domains: string[] }): JSX.Element {
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [label, setLabel] = useState('');
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fresh, setFresh] = useState<{ url: string; label: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = (): void => { void getShareLinks().then(setLinks).catch(() => setLinks([])); };
  useEffect(load, []);

  const create = (): void => {
    if (!label.trim()) { setError('Give it a name, so you know which one to revoke later.'); return; }
    setBusy(true); setError(null);
    void createShareLink({ label: label.trim(), domain: domain || null })
      .then((d) => { setFresh({ url: d.url, label: d.link.label }); setLabel(''); setDomain(''); load(); })
      .catch((e) => setError(e instanceof Error ? e.message : 'could not create that'))
      .finally(() => setBusy(false));
  };

  const revoke = (l: ShareLink): void => {
    if (!window.confirm(
      `Revoke "${l.label}"?\n\nThe link stops working immediately, and anyone using it right now `
      + 'is signed out. Nothing about your scans changes.',
    )) return;
    void revokeShareLink(l.id).then(load).catch(() => load());
  };

  const active = (links ?? []).filter((l) => l.state === 'active');
  const past = (links ?? []).filter((l) => l.state !== 'active');

  return (
    <section className="border border-warm-dark-2 rounded-xl p-5 mb-6">
      {/* No heading here: this panel IS a dashboard page now, and the page
          head above it already says what this is. Two titles in a row read
          as a mistake, because they are one. */}
      <p className="text-text-secondary text-sm leading-relaxed max-w-prose">
        Send somebody a link and they can see your scans, trends, questions and the Fix it plan, and
        export the report. They cannot run a scan, spend anything or change a question set. Useful for
        showing a client their own results without giving away your account.
      </p>

      {fresh && (
        <div className="mt-4 border border-cyan/40 rounded-lg p-3 bg-cyan/5">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-cyan mb-1">
            Copy this now, it is not shown again
          </p>
          <p className="font-mono text-sm break-all text-text-primary">{fresh.url}</p>
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={() => {
                void navigator.clipboard.writeText(fresh.url)
                  .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })
                  .catch(() => setError('could not copy, select the text instead'));
              }}
              className="font-mono text-xs text-cyan border border-cyan/40 rounded px-2.5 py-1 hover:bg-cyan/10 transition"
            >{copied ? 'copied ✓' : 'copy link'}</button>
            <button onClick={() => setFresh(null)} className="font-mono text-xs text-text-secondary hover:text-text-primary">
              done
            </button>
          </div>
          <p className="text-xs text-text-secondary mt-2 leading-relaxed">
            Anyone with this URL gets in, so send it the way you would send a password. You will get one
            email the first time it is opened, and you can revoke it below at any point.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mt-4">
        <label className="flex flex-col gap-1">
          <span className="font-mono text-xs text-text-secondary">Who is it for</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            // A role and an organisation, never a real person. A placeholder is
            // copy, and copy that names someone puts a third party's name in
            // front of every other client who opens this screen.
            placeholder="e.g. the agency managing this site"
            className="bg-warm-dark border border-warm-dark-2 rounded-lg px-3 py-2 text-sm w-56"
          />
        </label>
        {domains.length > 1 && (
          <label className="flex flex-col gap-1">
            <span className="font-mono text-xs text-text-secondary">Which site</span>
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="bg-warm-dark border border-warm-dark-2 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All of them</option>
              {domains.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </label>
        )}
        <button
          onClick={create}
          disabled={busy}
          className="bg-cyan text-warm-dark font-medium rounded-lg px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-40 transition"
        >{busy ? 'Creating…' : 'Create link'}</button>
      </div>
      {error && <p className="font-mono text-xs text-[#E0574A] mt-2">{error}</p>}

      {active.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-text-secondary font-mono text-xs text-left">
              <th className="py-1 pr-3">Who</th><th className="pr-3">Sees</th><th className="pr-3">Opened</th>
              <th className="pr-3">Expires</th><th></th>
            </tr></thead>
            <tbody>
              {active.map((l) => (
                <tr key={l.id} className="border-t border-warm-dark-2">
                  <td className="py-2 pr-3 text-text-primary">{l.label}</td>
                  <td className="pr-3 text-text-secondary">{l.domain ?? 'everything'}</td>
                  <td className="pr-3 font-mono text-xs text-text-secondary">
                    {l.useCount === 0
                      ? <span className="text-text-secondary/60">not yet</span>
                      : `${l.useCount}×, last ${new Date(l.lastSeenAt!).toLocaleDateString()}`}
                  </td>
                  <td className="pr-3 font-mono text-xs text-text-secondary">
                    {l.expiresAt ? new Date(l.expiresAt).toLocaleDateString() : 'never'}
                  </td>
                  <td className="text-right">
                    <button
                      onClick={() => revoke(l)}
                      className="font-mono text-xs text-text-secondary hover:text-[#E0574A]"
                    >revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {past.length > 0 && (
        <p className="font-mono text-xs text-text-secondary/70 mt-3">
          {past.length} older {past.length === 1 ? 'link is' : 'links are'} revoked or expired and no longer work.
        </p>
      )}

      {links !== null && active.length === 0 && !fresh && (
        <p className="font-mono text-xs text-text-secondary/70 mt-3">No active links. Nobody else can see this account.</p>
      )}
    </section>
  );
}
